import { Caption, Card, VBadge, VButton, VIconActionButton, cn } from '@vrzl/ui';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';

import type { OverviewBlock } from '../api/types';
import { blockWidthLabels } from '../constants';


type BlockPreviewCardProps = {
  block: OverviewBlock;
  index: number;
  count: number;
  label: string;
  nameByCode: ReadonlyMap<string, string>;
  selected: boolean;
  error: string | undefined;
  onSelect: () => void;
  /** Verplaats dit blok naar `to` (pijltjes). */
  onMove: (to: number) => void;
  onDragStart: () => void;
  /** De sleepactie is afgelopen, ook als er niet op een kaart is losgelaten. */
  onDragEnd: () => void;
};

/**
 * Een blok in "Indeling van de Overview": zelfde rasteritem als op de Overview, met
 * sleepgreep (HTML5 drag-and-drop), pijltjes en per criterium een plaatshouderbalk.
 */
export function BlockPreviewCard({
  block,
  index,
  count,
  label,
  nameByCode,
  selected,
  error,
  onSelect,
  onMove,
  onDragStart,
  onDragEnd,
}: BlockPreviewCardProps) {
  return (
      <Card
        padding="md"
        onClick={onSelect}
        className={cn(
          'flex flex-col gap-2',
          selected && 'border-neutral-400 bg-neutral-100',
          error && 'border-error-600',
        )}
      >
        <div className="flex items-center gap-1">
          <span
            draggable
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = 'move';
              // Firefox start geen drag zonder data.
              event.dataTransfer.setData('text/plain', String(index));
              onDragStart();
            }}
            onDragEnd={onDragEnd}
            className="cursor-grab text-muted-foreground"
            title="Sleep om te verplaatsen"
          >
            <GripVertical className="size-4" aria-hidden />
          </span>
          <VButton
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={selected}
            className="min-w-0 flex-1 justify-start px-1 font-semibold text-foreground"
            onClick={onSelect}
          >
            <span className="truncate">{label}</span>
          </VButton>
        </div>

        <div className="flex items-center gap-1">
          <VBadge color="neutral" className="shrink-0">
            {blockWidthLabels[block.width]}
          </VBadge>
          {/* Geen doorklik naar de kaart: die zou na het verplaatsen de oude positie selecteren. */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="ml-auto flex items-center" onClick={(event) => event.stopPropagation()}>
            <VIconActionButton
              icon={ChevronLeft}
              label={`Verplaats ${label} naar voren`}
              disabled={index === 0}
              onClick={() => onMove(index - 1)}
            />
            <VIconActionButton
              icon={ChevronRight}
              label={`Verplaats ${label} naar achteren`}
              disabled={index === count - 1}
              onClick={() => onMove(index + 1)}
            />
          </div>
        </div>

        {block.criteria.length === 0 ? (
          <Caption className="italic">Nog geen criteria</Caption>
        ) : (
          <ul>
            {block.criteria.map((code) => (
              <li key={code} className="border-t border-border py-2">
                <Caption>{nameByCode.get(code) ?? code}</Caption>
                <div aria-hidden className="mt-1 h-1.5 w-3/5 rounded-full bg-muted" />
              </li>
            ))}
          </ul>
        )}

        {error && <Caption className="text-error-800">{error}</Caption>}
      </Card>
  );
}
